/**
 * MapContext - Unified map provider combining viewer lifecycle and layer management
 *
 * Replaces CesiumContext and MapControllerProvider with a single entry point.
 * All mapping logic lives in features/mapping.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import MapController from './MapController';
import PersistentCesiumBackground from './PersistentCesiumBackground/PersistentCesiumBackground';

const MapContext = createContext(null);

export function useMap() {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
}

/**
 * MapProvider - Manages Cesium viewer and MapController for the entire application
 *
 * Provides: viewer, viewerReady, setCamera, layerController, addEntity, removeEntity, clearEntities
 */
export function MapProvider({ children }) {
  const [viewer, setViewer] = useState(null);
  const [viewerReady, setViewerReady] = useState(false);

  const currentViewRef = useRef({
    mode: 'globe',
    interactive: false,
    entities: [],
    terrain: false,
  });
  const entitiesRef = useRef([]);
  const mapControllerRef = useRef(null);

  const registerViewer = useCallback((cesiumViewer) => {
    console.log('🌍 MapContext: Registering viewer', cesiumViewer);
    setViewer(cesiumViewer);
    setViewerReady(true);
  }, []);

  const unregisterViewer = useCallback(() => {
    console.log('🌍 MapContext: Unregistering viewer');
    if (mapControllerRef.current) {
      mapControllerRef.current.cleanup().catch((err) => {
        console.error('MapController cleanup error:', err);
      });
      mapControllerRef.current = null;
    }
    setViewer(null);
    setViewerReady(false);
  }, []);

  const setCamera = useCallback(
    (viewConfig) => {
      if (!viewer) {
        console.warn('🌍 MapContext: Viewer not ready, cannot set view');
        return;
      }

      const {
        mode = 'globe',
        camera = null,
        interactive = false,
        entities = [],
        terrain = false,
        bounds = null,
        duration = 2.0,
      } = viewConfig;

      console.log('🌍 MapContext: Setting camera', viewConfig);
      const alreadyInMode = currentViewRef.current.mode === mode;
      if (
        alreadyInMode &&
        !camera &&
        !bounds &&
        (mode === 'globe' || mode === 'region')
      ) {
        if (viewer.scene) {
          viewer.scene.screenSpaceCameraController.enableRotate = interactive;
          viewer.scene.screenSpaceCameraController.enableTranslate = interactive;
          viewer.scene.screenSpaceCameraController.enableZoom = interactive;
          viewer.scene.screenSpaceCameraController.enableTilt = interactive;
          viewer.scene.screenSpaceCameraController.enableLook = interactive;
        }
        currentViewRef.current = { ...currentViewRef.current, interactive };
        return;
      }

      if (viewer.scene) {
        viewer.scene.screenSpaceCameraController.enableRotate = interactive;
        viewer.scene.screenSpaceCameraController.enableTranslate = interactive;
        viewer.scene.screenSpaceCameraController.enableZoom = interactive;
        viewer.scene.screenSpaceCameraController.enableTilt = interactive;
        viewer.scene.screenSpaceCameraController.enableLook = interactive;
      }

      if (camera) {
        viewer.camera.flyTo({
          destination: camera.destination || viewer.camera.position,
          orientation: camera.orientation,
          duration,
          easingFunction: window.Cesium?.EasingFunction.CUBIC_IN_OUT,
        });
      } else if (mode === 'globe') {
        viewer.camera.flyTo({
          destination: window.Cesium.Cartesian3.fromDegrees(
            -40.0,
            35.0,
            20000000
          ),
          orientation: {
            heading: 0.0,
            pitch: -window.Cesium.Math.PI_OVER_TWO,
            roll: 0.0,
          },
          duration,
          easingFunction: window.Cesium.EasingFunction.CUBIC_IN_OUT,
        });
      } else if (mode === 'region') {
        const regionBounds = bounds || {
          west: -71.0,
          south: 41.0,
          east: -66.0,
          north: 45.0,
        };
        viewer.camera.flyTo({
          destination: window.Cesium.Rectangle.fromDegrees(
            regionBounds.west,
            regionBounds.south,
            regionBounds.east,
            regionBounds.north
          ),
          duration,
          easingFunction: window.Cesium.EasingFunction.CUBIC_IN_OUT,
        });
      }

      if (entitiesRef.current.length > 0) {
        entitiesRef.current.forEach((entity) => {
          if (viewer.entities.contains(entity)) {
            viewer.entities.remove(entity);
          }
        });
        entitiesRef.current = [];
      }

      currentViewRef.current = { mode, interactive, entities, terrain };
    },
    [viewer]
  );

  const addEntity = useCallback(
    (entityConfig) => {
      if (!viewer) return null;
      const entity = viewer.entities.add(entityConfig);
      entitiesRef.current.push(entity);
      return entity;
    },
    [viewer]
  );

  const removeEntity = useCallback(
    (entity) => {
      if (!viewer || !entity) return;
      viewer.entities.remove(entity);
      entitiesRef.current = entitiesRef.current.filter((e) => e !== entity);
    },
    [viewer]
  );

  const clearEntities = useCallback(() => {
    if (!viewer) return;
    entitiesRef.current.forEach((entity) => {
      if (viewer.entities.contains(entity)) {
        viewer.entities.remove(entity);
      }
    });
    entitiesRef.current = [];
  }, [viewer]);

  const layerController = useMemo(() => {
    if (!viewer) return null;
    if (
      !mapControllerRef.current ||
      mapControllerRef.current.viewer !== viewer
    ) {
      console.log('🔄 MapContext: Creating MapController instance');
      mapControllerRef.current = new MapController(viewer, {});
    }
    return mapControllerRef.current;
  }, [viewer]);

  useEffect(() => {
    if (layerController && viewer && window.Cesium && !layerController.isReady()) {
      layerController.initialize({}).catch((err) => {
        console.error('MapController init error:', err);
      });
    }
  }, [layerController, viewer]);

  // Dev helper: Cmd+Shift+I logs current camera position for backend layout API config
  useEffect(() => {
    if (!viewer || !window.Cesium) return;
    const Cesium = window.Cesium;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
        e.preventDefault();
        const cam = viewer.camera;
        const cartographic = Cesium.Cartographic.fromCartesian(cam.position);
        const camera_lat = Cesium.Math.toDegrees(cartographic.latitude);
        const camera_lon = Cesium.Math.toDegrees(cartographic.longitude);
        const camera_height_m = cartographic.height;
        const camera_heading = cam.heading;
        const camera_pitch = cam.pitch;
        const camera_roll = cam.roll;
        const backendFormat = {
          camera_lat,
          camera_lon,
          camera_height_m,
          camera_heading,
          camera_pitch,
          camera_roll,
        };
        console.log('📷 Current camera (use for backend layout API):', backendFormat);
        console.log('📷 Copy-paste JSON:', JSON.stringify(backendFormat, null, 2));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewer]);

  const value = useMemo(
    () => ({
      viewer,
      viewerReady,
      get currentView() {
        return currentViewRef.current;
      },
      setCamera,
      setCesiumView: setCamera,
      layerController,
      addEntity,
      removeEntity,
      clearEntities,
    }),
    [
      viewer,
      viewerReady,
      setCamera,
      layerController,
      addEntity,
      removeEntity,
      clearEntities,
    ]
  );

  return (
    <MapContext.Provider value={value}>
      <PersistentCesiumBackground
        registerViewer={registerViewer}
        unregisterViewer={unregisterViewer}
      />
      {children}
    </MapContext.Provider>
  );
}
